/**
 * 74.20 (DECISIONS 427 (4), 439: the owner, on the sheet: "Riverbed looks pretty good"; the five leans taken): the drainage
 * drawn on the ground — no water, the beds the water would leave. The lines are core/drainageLines.ts's (shared with lane I's
 * creek cut through the woods); this is their look, one for the sheet (design_sheets/riverbeds.html) and the game:
 *
 * - **a gully** (a line): the ground darkened, its floor toward the climate's bed colour;
 * - **a bed** (by the climate: `damp` dark silt, `stony` grey cobbles, `wash` pale gravel), speckled, its bank eased in;
 * - **a broad bed**: a bed with its low-flow channel down the middle and a wider bank;
 * - **reeds** where the line runs flat and damp or meets a basin (rendering/groundScatter.ts's `reed` kind);
 * - **far off**, the beds and broad beds as strips on the planet's mesh (rendering/riverbedFar.ts).
 *
 * The tint is a cell's colour times `mul`, then toward `rgb` by `w` — kept per cell by the fine patch so its live colour
 * (rendering/fineTerrainPatch.ts colourCell) takes it at every scroll. Presentation only: no state, no version.
 */
import * as THREE from 'three';
import type { GameState, PlanetState, Vec3 } from '../types';
import { BED_KINDS, drainageLinesOf, type BedKind, type DrainageHit, type DrainageLines } from '../core/drainageLines';
import { biomesFor } from '../core/biomes';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';

/** The switch: the beds drawn in play (the sheet draws them regardless). */
export const RIVERBEDS = { on: true };

export const BED_LOOK = {
  damp: { floor: '#3a3226', speck: '#2a241b', light: '#5b5140', bank: '#3f4627', channel: '#231d15' },
  stony: { floor: '#7c776b', speck: '#55514a', light: '#a7a193', bank: '#666152', channel: '#4c4840' },
  wash: { floor: '#cbbb96', speck: '#a8977a', light: '#e2d6b8', bank: '#b9a985', channel: '#b5a27d' },
  /** a gully: the ground darkened by this, toward the kind's floor by `toward` */
  gully: { dark: 0.62, toward: 0.35 },
  /** the speckle's share of the floor, the low-flow channel's share of a broad bed's width, the speckle's cells a person */
  speck: 0.28, channel: 0.22, speckPerPerson: 3.1,
  reed: { stem: '#5d7236', tip: '#a49a62', heightPeople: [0.9, 1.5], clumpsPerSide: 2, offsetPeople: [0.2, 1.6], blades: 7 },
};

type Rgb = [number, number, number];
const rgbOf = (s: string): Rgb => { const c = new THREE.Color(s); return [c.r, c.g, c.b]; };
const LOOK_RGB = Object.fromEntries(BED_KINDS.map(k => [k, Object.fromEntries(Object.entries(BED_LOOK[k]).map(([n, v]) => [n, rgbOf(v)]))])) as Record<BedKind, Record<'floor' | 'speck' | 'light' | 'bank' | 'channel', Rgb>>;

/**
 * Where the beds are drawn, for the fine patch (`setRiverbeds`) and the far strips: a dressed world's (one the woods would
 * stand on — `biomesFor`), at the human scale, the lines `drainageLinesOf` with the match's wind seed (the woods' own: the
 * same cached lines); the battle planet's (`warm`) built now, at the caller's setup — another's when the eye first goes
 * there. Off (`RIVERBEDS.on`) or the planet scale: none.
 */
export interface RiverbedSource {
  /** a planet's lines, or null where none are drawn */
  linesOf: (planet: PlanetState) => DrainageLines | null;
  /** the planets they are drawn on (indices), and the person */
  planets: number[]; person: number;
}
export function riverbedSource(state: GameState, warm: number): RiverbedSource | null {
  const ps = personSetOf(state);
  if (!RIVERBEDS.on || ps.k >= 1) return null;
  const person = PERSON_BODY_HEIGHT * ps.k, windSeed = String(state.config.seed ?? '');
  const drawn = (p: PlanetState) => p.type !== 'star' && p.type !== 'wormhole' && biomesFor(p).length > 0;
  const planets = state.planets.flatMap((p, i) => (drawn(p) ? [i] : []));
  if (!planets.length) return null;
  if (planets.includes(warm)) drainageLinesOf(state.planets[warm], person);
  return { linesOf: p => (drawn(p) ? drainageLinesOf(p, person) : null), planets, person };
}

/** A small hash in [0, 1) (drawing only). */
export function bedHash(a: number, b: number): number { const v = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453; return v - Math.floor(v); }

/** The speckle at a point, fixed to the world (so a scrolled patch keeps it): two cells of `speckPerPerson` a person. */
export function bedSpeck(dir: Vec3, radius: number, person: number): number {
  const s = (radius / person) * BED_LOOK.speckPerPerson;
  return bedHash(Math.floor(dir.x * s + dir.z * 0.37 * s), Math.floor(dir.y * s - dir.z * 0.61 * s));
}

/** A cell's tint: its colour × `mul`, then toward `rgb` by `w`. */
export interface BedTint { mul: number; w: number; r: number; g: number; b: number }

const lerp3 = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/**
 * The tint at a hit (`speck`: bedSpeck's, 0–1), written to `out`. The sheet's rule: a gully darkens toward its floor; a bed's
 * floor is the kind's (speckled, a broad bed's channel down its middle), eased into the bank at its edge; the bank leans the
 * ground toward the kind's bank colour, the more by the bed. No hit: the identity (mul 1, w 0).
 */
export function bedTint(h: DrainageHit | null, speck: number, out: BedTint): BedTint {
  out.mul = 1; out.w = 0; out.r = out.g = out.b = 0;
  if (!h) return out;
  const L = LOOK_RGB[h.kind];
  let t: Rgb;
  if (h.cls === 'line') {
    if (h.edge <= 0) { out.mul = BED_LOOK.gully.dark; out.w = BED_LOOK.gully.toward; t = L.floor; }
    else { const f = h.edge / h.bank; out.mul = BED_LOOK.gully.dark + (1 - BED_LOOK.gully.dark) * Math.min(1, 0.5 + 0.5 * f); return out; }
  } else if (h.edge <= 0) {
    let c = L.floor;
    if (speck < BED_LOOK.speck) c = lerp3(c, L.speck, 0.8); else if (speck > 1 - BED_LOOK.speck * 0.6) c = lerp3(c, L.light, 0.6);
    if (h.cls === 'broad' && Math.abs(h.across) < h.half * BED_LOOK.channel) c = lerp3(c, L.channel, 0.75);
    // (out = base.lerp(bank, 0.85).lerp(c, e): base × (1 − w) + t × w, w = 1 − 0.15 (1 − e))
    const e = Math.min(1, -h.edge / Math.max(0.3, 0.25 * h.half)), w = 1 - 0.15 * (1 - e);
    t = [(L.bank[0] * 0.85 * (1 - e) + c[0] * e) / w, (L.bank[1] * 0.85 * (1 - e) + c[1] * e) / w, (L.bank[2] * 0.85 * (1 - e) + c[2] * e) / w];
    out.w = w;
  } else {
    const f = Math.min(1, h.edge / h.bank);
    out.w = (1 - f) * (h.cls === 'broad' ? 0.9 : 0.7); t = L.bank;
  }
  out.r = t[0]; out.g = t[1]; out.b = t[2];
  return out;
}

/** A colour (r, g, b at `i` in `a`) tinted in place. */
export function applyBedTint(a: Float32Array, i: number, t: { mul: number; w: number; r: number; g: number; b: number }): void {
  const m = t.mul * (1 - t.w);
  a[i] = a[i] * m + t.r * t.w; a[i + 1] = a[i + 1] * m + t.g * t.w; a[i + 2] = a[i + 2] * m + t.b * t.w;
}

/** A clump of reeds a unit tall: thin tapering blades leaning out, green below, tan tips (vertex colours; the instance's tint scales them). */
export function reedClumpGeometry(): THREE.BufferGeometry {
  const L = BED_LOOK.reed, pos: number[] = [], colr: number[] = [], stem = new THREE.Color(L.stem), tip = new THREE.Color(L.tip);
  for (let b = 0; b < L.blades; b++) {
    const a = (b / L.blades) * Math.PI * 2 + 0.3 * bedHash(b, 1), lean = 0.12 + 0.18 * bedHash(b, 2), h = 0.75 + 0.25 * bedHash(b, 3), wdt = 0.025;
    const bx = Math.cos(a) * 0.06, bz = Math.sin(a) * 0.06, tx = bx + Math.cos(a) * lean, tz = bz + Math.sin(a) * lean, px = -Math.sin(a) * wdt, pz = Math.cos(a) * wdt;
    pos.push(bx - px, 0, bz - pz, bx + px, 0, bz + pz, tx, h, tz);
    colr.push(stem.r, stem.g, stem.b, stem.r, stem.g, stem.b, tip.r, tip.g, tip.b);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setAttribute('color', new THREE.Float32BufferAttribute(colr, 3));
  g.computeVertexNormals();
  return g;
}
